import './App.css';
import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import BottomNav from './components/BottomNav';
import Login from './components/Login';
import GlobalInputClear from './components/GlobalInputClear';

import Home from './pages/home/Home';
import Community from './pages/community/Community';
import Dashboard from './pages/dashboard/Dashboard';
import Subscription from './pages/subscription/Subscription';
import ResetPassword from './pages/reset-password/ResetPassword';
import Terms from './pages/legal/Terms';
import Privacy from './pages/legal/Privacy';

// 브라우저 자동 scrollRestoration 비활성화 — 라우트 전환 시 우리가 직접 (0,0) 으로 보내는데
// 자동 복원이 이전 위치로 되돌리는 경우가 있어 모바일에서 'top 으로 안 올라가는' 증상 유발.
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

/* 탭(라우트) '이동' 시에만 화면을 맨 위로. 최초 마운트(새로고침)에는 손대지 않아
   브라우저 기본 스크롤 복원을 그대로 둠 → 새로고침 시 상단 빈 공간 회귀 방지.

   크롬 모바일: input 포커스 시 visual viewport 가 위로 올라가고 키보드 dismiss 후에도
   그 위치가 남아있는 경우가 있음. 라우트 전환 시 ScrollToTop 이 한 번 호출되어도
   새 페이지 layout 이 끝나기 전에 실행되어 무효화될 수 있어 — useLayoutEffect 로 commit
   직후 동기 실행 + RAF 로 한 프레임 뒤 한 번 더 reset 하여 layout 완료 후에도 적용. */
/* 구 URL 호환: /subscription → /profile 로 query 보존하며 리다이렉트 */
function LegacySubscriptionRedirect() {
  const { search, hash } = useLocation();
  return <Navigate to={`/profile${search}${hash}`} replace />;
}

/* 정의되지 않은 경로(존재하지 않는 URL) 접근 시 안내 alert 후 홈으로 이동.
   alert 는 렌더 중이 아니라 effect 에서 한 번만 호출. */
function NotFoundRedirect() {
  const navigate = useNavigate();
  // StrictMode(개발)에서 effect 가 2번 실행돼 alert 가 중복되는 것 방지 — 같은 인스턴스라 ref 유지됨
  const firedRef = useRef(false);
  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    alert('존재하지 않는 페이지입니다. 홈으로 이동합니다.');
    navigate('/', { replace: true });
  }, [navigate]);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  const isFirst = useRef(true);
  useLayoutEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    const reset = () => {
      window.scrollTo(0, 0);
      if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    reset();
    const raf = requestAnimationFrame(reset);
    return () => cancelAnimationFrame(raf);
  }, [pathname]);
  return null;
}

function App() {
  // 하단 네비에서 로그인 필요한 탭(대시보드/프로필) 클릭 시 띄우는 전역 로그인 모달
  const [showLogin, setShowLogin] = useState(false);

  // 새 배포 자동 반영 — index.html 의 번들 hash 와 현재 로드된 번들 hash 를 비교해
  // 다르면 자동 새로고침. 모바일 백그라운드/재시작 후 디스크 캐시로 옛 번들을 그대로 띄우는
  // 문제를 시간 기반 휴리스틱 대신 실제 버전 체크로 확실히 처리.
  useEffect(() => {
    let cancelled = false;
    let reloading = false;

    const getCurrentBundles = () => {
      const urls = [];
      Array.from(document.scripts).forEach((s) => {
        if (s.src && s.src.includes('/static/js/')) urls.push(s.src);
      });
      Array.from(document.querySelectorAll('link[rel="stylesheet"]')).forEach((l) => {
        if (l.href && l.href.includes('/static/css/')) urls.push(l.href);
      });
      // path 부분만 (오리진/쿼리 제거) — hash 비교를 위해
      return urls
        .map((u) => {
          try {
            return new URL(u).pathname;
          } catch {
            return u;
          }
        })
        .sort();
    };

    const checkForNewBuild = async () => {
      if (reloading || cancelled) return;
      try {
        // 강한 캐시 버스팅 — query + Cache-Control/Pragma 헤더 + cache:reload
        // (일부 CDN/모바일 브라우저는 no-store 만으론 캐시를 우회 못 함)
        const cb = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const res = await fetch(`/index.html?cb=${cb}`, {
          cache: 'reload',
          credentials: 'same-origin',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
          },
        });
        if (!res.ok || cancelled) return;
        const html = await res.text();
        const latest = Array.from(
          html.matchAll(/\/static\/(?:js|css)\/[^"'\s]+\.(?:js|css)/g)
        )
          .map((m) => m[0])
          .filter((p) => !p.endsWith('.map'));
        if (latest.length === 0) return;
        const currentPaths = getCurrentBundles().filter((p) => !p.endsWith('.map'));
        const allPresent = currentPaths.every((p) =>
          latest.some((l) => p.endsWith(l))
        );
        if (!allPresent && !cancelled) {
          reloading = true;
          window.location.reload();
        }
      } catch {
        // 네트워크 오류는 조용히 무시
      }
    };

    if (document.visibilityState === 'visible') {
      checkForNewBuild();
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') checkForNewBuild();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', checkForNewBuild);
    window.addEventListener('pageshow', checkForNewBuild);
    // 사용자가 한 자리에 오래 머무는 케이스(이벤트가 안 뜨는 경우) 대비 — 10분 폴링
    const pollId = setInterval(() => {
      if (document.visibilityState === 'visible') checkForNewBuild();
    }, 10 * 60 * 1000);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', checkForNewBuild);
      window.removeEventListener('pageshow', checkForNewBuild);
      clearInterval(pollId);
    };
  }, []);

  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <div className="App">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/community" element={<Community />} />
            {/* 도구 페이지는 커뮤니티 탭으로 통합됨 — 옛 링크는 커뮤니티로 리다이렉트 */}
            <Route path="/tools" element={<Navigate to="/community" replace />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route path="/profile" element={<Subscription />} />
            {/* 비밀번호 재설정 (PG사 심사용 — 재설정 메일 링크의 도착지) */}
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* 서비스 이용약관 / 개인정보 처리방침 */}
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            {/* 구 URL 호환 — Polar 결제 successUrl 등 외부 콜백이 살아 있을 수 있어 보존.
                ?success=true 같은 query 도 그대로 넘김. */}
            <Route
              path="/subscription"
              element={<LegacySubscriptionRedirect />}
            />
            {/* 정의되지 않은 모든 경로 — alert 후 홈으로 */}
            <Route path="*" element={<NotFoundRedirect />} />
          </Routes>
          <BottomNav onRequireLogin={() => setShowLogin(true)} />
          <Login isOpen={showLogin} onClose={() => setShowLogin(false)} />
          <GlobalInputClear />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
